import React, { useState, useEffect, useMemo } from "react";
import { Container, TextField, Card, Box } from "@material-ui/core";
import { Lang, Save } from "components";
import { useText, useGetData } from "hooks";
import useStore from "context";
import * as Api from "service/index";
import { useTranslation } from "localization/translation";
import Button from "@material-ui/core/Button";
import DeleteIcon from "@material-ui/icons/Delete";
import { GetUniqueId } from 'helpers/getUniqueId';

const initForm = {
  title_en: "",
  title_ru: "",
  title_hy: "",
};

const initItem = {
  title_en : "",
  title_hy : "",
  title_ru : "",
  description_en : "",
  description_hy : "",
  description_ru : "",
  image : "",
  link : "",
  order : 1,
}

const ClientsSection = () => {
  const [data, loading, refresh] = useGetData(Api.v2ClientsSection.getAll);

  const { lang } = useStore();
  const translation = useTranslation();

  const [text, setText, textChange, input] = useText(initForm);

  const [items, setItems] = useState([]);
  const [deleteItems, setDeleteItems] = useState([]);
  const [newItems, setNewItems] = useState([]);
  const [changedItems, setChangedItems] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [errorMsgs, setErrorMsgsState] = useState([]);

  useEffect(() => {
    if (data) {
      setText({
        title_en: data.title_en,
        title_ru: data.title_ru,
        title_hy: data.title_hy,
      });
      setItems(data.items || []);
    }
  }, [data]);


  const isButtonDisabled = useMemo(() => {
    if(!text.title_en || !text.title_hy || !text.title_ru) {
      return true;
    }
    if(data.title_en === text.title_en && data.title_hy === text.title_hy && data.title_ru === text.title_ru && deleteItems.length === 0 && newItems.length === 0 && changedItems.length === 0) {
      return true;
    }
    return false;
  }, [data, text, deleteItems, newItems, changedItems]);


  const checkAllFields = () => {
    let errors = [];

    if (newItems && newItems.length) {
      newItems.forEach((item, index) => {
        if(!item.title_hy || !item.title_en || !item.title_ru || !item.image || item.order === undefined || item.order === null) {
          errors.push({
            id: item.id,
            message: `Please fill all fields`
          });
        }
      });
    }
    if (changedItems && changedItems.length) {
      changedItems.forEach((item, index) => {
        if(!item.title_hy || !item.title_en || !item.title_ru || !item.image || item.order === undefined || item.order === null) {
          errors.push({
            id: item.id,
            message: `Please fill all fields`
          });
        }
      });
    }
    
    setErrorMsgsState(errors);
    
    return errors.length === 0;
  };


  const submitForm = async (e) => {
    e.preventDefault();

    if(!text.title_en || !text.title_hy || !text.title_ru) {
      setErrorMsg("Please fill all title fields");
      return;
    }
    if(!checkAllFields()) {
      return;
    }

    try {
      await Api.v2ClientsSection.update({
        title: text,
        delete_items: deleteItems,
        new_items: newItems,
        changed_items: changedItems,
      });

      setDeleteItems([]);
      setNewItems([]);
      setChangedItems([]);
      refresh();
    } catch (err) {
      setErrorMsg(err?.message);
    }
  };

  const getLastOrder = () => {
    if(items.length === 0) {
      return 1;
    }
    const sortedItems = [...items].sort((a, b) => b.order - a.order);
    return sortedItems[0].order + 1;
  } 

  const checkAllItemIsFill = () => {
    if(items.length === 0) {
      return true;
    }
    let isAllFill = true;
    items.forEach((item) => {
      if(!item.title_hy || !item.title_en || !item.title_ru || !item.image || item.order === undefined || item.order === null) {
        isAllFill = false;
      }
    })
    return isAllFill;
  }
  
  const handleAddNewItem = () => {
    if(checkAllItemIsFill()) {
      const newItem = {
        ...initItem,
        id: GetUniqueId(),
        order: getLastOrder(),
        is_new: true,
      };
      setItems([
        ...items,
        newItem
      ]);
      setNewItems((prev) => [...prev, newItem]);
    }
  }

  const handleDeleteItem = (id) => {
    const deletedItem = items.find(item => item.id === id);
    setItems(items.filter(item => item.id !== id));
    if(deletedItem?.is_new) {
      setNewItems(newItems.filter(item => item.id !== id));
      return;
    }
    if(changedItems.find(item => item.id === id)) {
      setChangedItems(changedItems.filter(item => item.id !== id));
    }
    setDeleteItems((prev) => [...prev, id]);
  }

  const getRelativePath = (url) => {
    try {
      if (url.startsWith("http")) {
        const { pathname } = new URL(url);
        return pathname.replace(/^\/public/, "");
      }
  
      return url.replace(/^\/public/, "");
    } catch (err) {
      return url;
    }
  };

  const handleChangeInput = (id, key, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
  
        return {
          ...item,
          [key]: key === "image" ? getRelativePath(value) : value,
        };
      })
    );
    if(newItems.find(item => item.id === id)) {
      setNewItems((prev) =>
        prev.map((item) => {
          if (item.id !== id) return item;
    
          return {
            ...item,
            [key]: key === "image" ? getRelativePath(value) : value,
          };
        })
      );
      return;
    }

    setChangedItems((prev) => {
      const existingItem = prev.find((item) => item.id === id);
      if (existingItem) {
        return prev.map((item) =>
          item.id === id ? { ...item, [key]: value } : item
        );
      } else {
        const changedItem = items.find((item) => item.id === id);
        if (changedItem) {
          return [...prev, { ...changedItem, [key]: value }];
        }
        return prev;
      }
    });
  };

  return (
    <Container>
      <Card className="shadow-xxl px-4 py-2">
        <Box component="form" onSubmit={submitForm}>
          <Lang />
          <TextField {...input(`title_${lang}`)} label={translation.title} />
          {
            items?.map((item, index) => {
              return (
                <Card
                  className="shadow-xxl px-4 py-4 mtb15 d-flex flex-column gap-3"
                  key={index}
                >
                  <div className="client-item-lang-button-wrapper">
                    <div className="lang-wrapper">
                      <Lang />
                    </div>
                    <Button
                      onClick={() => handleDeleteItem(item.id)}
                      className="delete-btn btn-neutral-danger mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center"
                    >
                      <DeleteIcon color="error" />
                    </Button>
                  </div>
                  
                  <div className="client-item-inputs-wrapper">
                    <TextField
                      label={translation.title}
                      value={item[`title_${lang}`]}
                      name={item[`title_${lang}`]}
                      onChange={(e) => handleChangeInput(item.id, `title_${lang}`, e.target.value)}
                      className="client-item-input"
                      required
                    />
                    <TextField
                      label={translation.image}
                      value={item.image}
                      name="image"
                      onChange={(e) => handleChangeInput(item.id, "image", e.target.value)}
                      className="client-item-input"
                      required
                    />
                  </div>
                  <div className="client-item-inputs-wrapper">
                    <TextField
                      label={translation.description}
                      value={item[`description_${lang}`]}
                      name={item[`description_${lang}`]}
                      onChange={(e) => handleChangeInput(item.id, `description_${lang}`, e.target.value)}
                      className="client-item-input"
                    />
                    <TextField
                      label={translation.link}
                      value={item.link}
                      name="link"
                      onChange={(e) => handleChangeInput(item.id, "link", e.target.value)}
                      className="client-item-input"
                    />
                  </div>
                  <div>
                    <TextField
                      label={translation.display_order}
                      value={item.order}
                      name="order"
                      onChange={(e) => handleChangeInput(item.id, "order", e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    {
                      errorMsgs.find(error => error.id === item.id) && (
                        <div className="error-msg">
                          {errorMsgs.find(error => error.id === item.id)?.message}
                        </div>
                      )
                    }
                  </div>
                </Card>
              )
            })
          }
          {
            checkAllItemIsFill() && (
              <Button
                className="m-2 btn-neutral-primary"
                onClick={handleAddNewItem}
              >
                {translation.add}
              </Button>
            )
          }
          {
            errorMsg && (
              <div className="error-msg">
                {errorMsg}
              </div>
            )
          }
          <Save isDisable={isButtonDisabled} />
        </Box>
      </Card>
    </Container>
  );
};

export default ClientsSection;
